import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import GlobalBusyIndicator from "@/components/GlobalBusyIndicator";
import NewsletterPopup from "@/components/NewsletterPopup";
import AnalyticsTracker from "@/components/AnalyticsTracker";

import Landing from "@/pages/Landing";
import Impressum from "@/pages/Impressum";
import CGV from "@/pages/CGV";
import Confidentialite from "@/pages/Confidentialite";
import APropos from "@/pages/APropos";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";
import EventsPage from "@/pages/Events";
import BookingPage from "@/pages/Booking";
import PricingPage from "@/pages/Pricing";
import AccompagnementsPage from "@/pages/Accompagnements";
import LoginPage from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import RegisterPage from "@/pages/Register";
import DashboardPage from "@/pages/Dashboard";
import CommunityPage from "@/pages/Community";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import GiftPage from "@/pages/Gift";
import GiftClaim from "@/pages/GiftClaim";
import AdminPage from "@/pages/Admin";
import StoryPage from "@/pages/Story";
import LogoPage from "@/pages/Logo";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import EventDetail from "@/pages/EventDetail";
import LangageDeLumiere from "@/pages/LangageDeLumiere";
import Transmissions from "@/pages/Transmissions";
import TransmissionDetail from "@/pages/TransmissionDetail";
import WelcomeGift from "@/pages/WelcomeGift";
import NotFound from "@/pages/NotFound";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) {
        return <div className="min-h-screen flex items-center justify-center" data-testid="loading"><div className="text-muted-foreground">Chargement…</div></div>;
    }
    if (!user) return <Navigate to="/connexion" replace />;
    return children;
}

function Shell() {
    return (
        <div className="App">
            <Header />
            <main className="pt-20">
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/a-propos" element={<APropos />} />
                    <Route path="/langage-de-lumiere" element={<LangageDeLumiere />} />
                    <Route path="/histoire" element={<StoryPage />} />
                    <Route path="/logo" element={<LogoPage />} />
                    <Route path="/journal" element={<Blog />} />
                    <Route path="/journal/:slug" element={<BlogArticle />} />
                    <Route path="/transmissions" element={<Transmissions />} />
                    <Route path="/transmissions/:slug" element={<TransmissionDetail />} />
                    <Route path="/evenements" element={<EventsPage />} />
                    <Route path="/evenements/:id" element={<EventDetail />} />
                    <Route path="/reservation" element={<BookingPage />} />
                    <Route path="/accompagnements" element={<AccompagnementsPage />} />
                    <Route path="/abonnements" element={<PricingPage />} />
                    <Route path="/offrir" element={<GiftPage />} />
                    <Route path="/cadeau/:code" element={<GiftClaim />} />
                    <Route path="/connexion" element={<LoginPage />} />
                    <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
                    <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
                    <Route path="/inscription" element={<RegisterPage />} />
                    <Route path="/espace" element={<Protected><DashboardPage /></Protected>} />
                    <Route path="/communaute" element={<Protected><CommunityPage /></Protected>} />
                    <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/mentions-legales" element={<Impressum />} />
                    <Route path="/cgv" element={<CGV />} />
                    <Route path="/confidentialite" element={<Confidentialite />} />
                    <Route path="/newsletter/desinscription" element={<NewsletterUnsubscribe />} />
                    <Route path="/cadeau-bienvenue" element={<WelcomeGift />} />
                    {/* Legacy / mistyped URL redirects — avoids 404 noindex loss of link equity. */}
                    <Route path="/ateliers" element={<Navigate to="/evenements" replace />} />
                    <Route path="/atelier" element={<Navigate to="/evenements" replace />} />
                    <Route path="/workshops" element={<Navigate to="/evenements" replace />} />
                    {/* Catch-all — noindex 404 page (prevents soft-404s in GSC) */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            <Footer />
            <CookieBanner />
            <NewsletterPopup />
            <Toaster richColors position="top-right" />
            <GlobalBusyIndicator />
        </div>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <AuthProvider>
                <Shell />
            </AuthProvider>
        </BrowserRouter>
    );
}
